import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import * as Sentry from '@sentry/react'

import { Button } from '../../../../../components/Button/Button'
import { IntegrationFileBrowser } from '../../../../../components/DocumentFileBrowser/IntegrationFileBrowser'
import { getAvailableFileName, getNextFileName } from '../../../../../components/DocumentFileBrowser/utils'
import { getUserDisplayName } from '../../common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../common/tracking'
import { getLinkedFolderIds } from '../../../../../common/linkedDocumentFolders'
import { DocumentFolderMapSource, useLinkContactFolder } from '../../../../../common/useLinkContactFolder'
import { type User } from '../../../../../models/User'
import { type DocumentExportType } from '../../../../../components/DocumentConnector/models'
import { IntegrationSavedModalBody } from '../../../../../components/Integrations/IntegrationSavedModalBody/IntegrationSavedModalBody'
import { getIntegrationDisplayName, type IntegrationServiceId } from '../../../../../common/integrations'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import {
  useUploadDocumentConnectorFileMutation,
  type DocumentConnectorAnchor,
  type DocumentConnectorItem,
} from '../../../../../store/documentConnectors/documentConnectors'
import {
  resetFileBrowser,
  selectFileBrowserBreadcrumbs,
  selectFileBrowserCurrentItems,
} from '../../../../../store/documentFileBrowser/documentFileBrowserSlice'
import { addToast } from '../../../../../store/toast/toastSlice'
import { ErrorReasonCodeToMessage } from '../../common/models'
import { type SyncStatusView } from '../ExternalUpdates/models'
import styles from './SaveToDocumentConnectorModal.module.scss'

const ensurePdfExtension = (name: string): string => (name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`)

interface SaveToDocumentConnectorModalProps {
  serviceId: IntegrationServiceId
  show: boolean
  onHide: () => void
  generatePDFBlob: () => Promise<Blob | null>
  defaultFileName?: string
  initialFolderIds?: string[]
  initialFoldersReady?: boolean
  anchor?: DocumentConnectorAnchor | null
  saveSyncStatus?: boolean
  onSaved?: (uploaded: DocumentConnectorItem) => void | Promise<void>
  showSuccessState?: boolean
  showSuccessSidebar?: boolean
  contact?: User | null
  onContactUpdated?: (user: User) => void
  exportType: DocumentExportType
}

export const SaveToDocumentConnectorModal: React.FC<SaveToDocumentConnectorModalProps> = ({
  serviceId,
  show,
  onHide,
  generatePDFBlob,
  defaultFileName = '',
  initialFolderIds,
  initialFoldersReady,
  anchor,
  saveSyncStatus,
  onSaved,
  showSuccessState = true,
  showSuccessSidebar = true,
  contact,
  onContactUpdated,
  exportType,
}) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const breadcrumbs = useAppSelector(selectFileBrowserBreadcrumbs)
  const currentItems = useAppSelector(selectFileBrowserCurrentItems)
  const [uploadFile] = useUploadDocumentConnectorFileMutation()
  const { trackingClient } = useTracking()
  const handleLinkFolder = useLinkContactFolder(contact ?? null, DocumentFolderMapSource.EXPORT, onContactUpdated)

  const displayName = getIntegrationDisplayName(serviceId)
  const contactName = contact ? getUserDisplayName(contact) : undefined
  const linkedFolderId = contact ? (getLinkedFolderIds(contact, serviceId)[0] ?? null) : null

  const [fileName, setFileName] = useState<string>(defaultFileName)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [savedSyncStatus, setSavedSyncStatus] = useState<SyncStatusView | null>(null)

  useEffect(() => {
    return () => {
      dispatch(resetFileBrowser())
    }
  }, [dispatch])

  const currentFolder = breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 1] : null
  const canSave = currentFolder !== null && fileName.trim().length > 0

  const existingFileNames = useMemo(() => currentItems.filter((item) => item.type === 'file').map((item) => item.name), [currentItems])

  const resolveFileName = useCallback(() => {
    setFileName((current) => {
      const trimmed = current.trim()
      if (!trimmed) {
        return current
      }
      return getAvailableFileName(ensurePdfExtension(trimmed), existingFileNames)
    })
  }, [existingFileNames])

  useEffect(() => {
    resolveFileName()
  }, [currentFolder?.id, resolveFileName])

  const handleHide = useCallback(() => {
    setFileName(defaultFileName)
    setSavedSyncStatus(null)
    onHide()
  }, [defaultFileName, onHide])

  const handleSave = useCallback(async () => {
    if (!currentFolder) {
      return
    }

    setIsSaving(true)
    try {
      const blob = await generatePDFBlob()
      if (!blob) {
        throw new Error('Failed to generate PDF')
      }

      const fullFileName = getAvailableFileName(ensurePdfExtension(fileName), existingFileNames)

      const uploaded = await uploadFile({
        accountId,
        connector: serviceId,
        file: blob,
        fileName: fullFileName,
        folderId: currentFolder.id,
        contentType: 'application/pdf',
        anchor,
        saveSyncStatus,
      }).unwrap()

      trackingClient.track({ eventName: TrackEvent.DOCUMENT_EXPORTED, eventDetails: { system: serviceId, type: exportType } })

      if (showSuccessState) {
        setSavedSyncStatus({
          serviceId,
          syncedAt: new Date().toISOString(),
          location: uploaded?.webUrl ?? undefined,
          type: 'aiResult',
          name: uploaded?.name ?? fullFileName,
        })
      } else {
        dispatch(
          addToast({
            variant: 'success',
            title: `Saved to ${displayName}`,
            messages: [`'${uploaded?.name ?? fullFileName}' was successfully saved to ${displayName}.`],
          }),
        )
        handleHide()
      }

      try {
        await onSaved?.(uploaded)
      } catch (e) {
        Sentry.captureException(e)
      }
    } catch (e) {
      Sentry.captureException(e)
      const status = (e as { status?: number }).status
      if (status === 409) {
        setFileName((current) => getNextFileName(ensurePdfExtension(current.trim())))
        dispatch(addToast({ variant: 'error', title: 'Error', messages: [ErrorReasonCodeToMessage[409]] }))
      } else {
        dispatch(addToast({ variant: 'error', title: 'Error', messages: [`Failed to save file to ${displayName}.`] }))
      }
    } finally {
      setIsSaving(false)
    }
  }, [
    currentFolder,
    generatePDFBlob,
    fileName,
    existingFileNames,
    uploadFile,
    accountId,
    serviceId,
    anchor,
    saveSyncStatus,
    onSaved,
    dispatch,
    displayName,
    showSuccessState,
    handleHide,
    trackingClient,
    exportType,
  ])

  return (
    <Modal
      show={show}
      onHide={handleHide}
      centered
      size={savedSyncStatus && showSuccessSidebar ? 'xl' : undefined}
      dialogClassName={savedSyncStatus ? undefined : styles.saveModal}
    >
      {savedSyncStatus ? (
        <Modal.Body className='p-0'>
          <IntegrationSavedModalBody syncStatuses={[savedSyncStatus]} serviceId={serviceId} onHide={handleHide} showSidebar={showSuccessSidebar} />
        </Modal.Body>
      ) : (
        <>
          <Modal.Header closeButton>
            <Modal.Title>Save to {displayName}</Modal.Title>
          </Modal.Header>
          <Modal.Body className={styles.saveModalBody}>
            <div className={styles.folderBrowserArea}>
              <IntegrationFileBrowser
                serviceId={serviceId}
                showDisabledFiles
                selectionMode='single-folder'
                initialFolderIds={initialFolderIds}
                initialFoldersReady={initialFoldersReady}
                contactName={contactName}
                linkedFolderId={linkedFolderId}
                onLinkFolder={contact ? handleLinkFolder : undefined}
              />
            </div>
            <div className={styles.fileNameArea}>
              <Form.Label className='lead-extra-small mb-1'>File name</Form.Label>
              <Form.Control type='text' value={fileName} onChange={(e) => setFileName(e.target.value)} onBlur={resolveFileName} placeholder='Enter file name' />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondaryGhostButton' onClick={handleHide}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleSave} disabled={!canSave} loading={isSaving}>
              Save file
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  )
}
